
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Parent } from '@/types/models';
import { formatPhoneNumber } from '@/lib/format';

// Schema for parent form validation
const parentSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email format").optional().or(z.literal('')),
  phone: z.string().optional().or(z.literal('')),
  isPrimary: z.boolean().optional()
});

export type ParentFormValues = z.infer<typeof parentSchema>;

interface AddParentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onParentAdded: (parent: Omit<Parent, 'id' | 'created_at' | 'invite_status' | 'user_id'>, continueToDetails?: boolean) => void;
  familyId: string;
  isLoading: boolean;
  familyName?: string;
}

const AddParentDialog: React.FC<AddParentDialogProps> = ({
  open,
  onOpenChange,
  onParentAdded,
  familyId,
  isLoading,
  familyName
}) => {
  const { register, handleSubmit, reset, formState: { errors }, setValue, watch } = useForm<ParentFormValues>({
    resolver: zodResolver(parentSchema),
    defaultValues: {
      firstName: '',
      lastName: familyName || '',
      email: '',
      phone: '',
      isPrimary: false
    }
  });

  const isPrimary = watch('isPrimary');

  const [formattedPhone, setFormattedPhone] = useState('');

  // Update lastName when familyName changes or when dialog opens
  useEffect(() => {
    if (open && familyName) {
      setValue('lastName', familyName);
    }
  }, [open, familyName, setValue]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formatted = formatPhoneNumber(value);
    setFormattedPhone(formatted);
    setValue('phone', formatted);
  };

  const createParentData = (data: ParentFormValues) => ({
    first_name: data.firstName,
    last_name: data.lastName,
    email: data.email || '',
    phone: data.phone || '',
    is_primary: data.isPrimary || false,
    family_id: familyId,
    organization_id: '' // This will be filled in by the handler
  });

  const resetForm = () => {
    reset({
      firstName: '',
      lastName: familyName || '',
      email: '',
      phone: '',
      isPrimary: false
    });
    setFormattedPhone('');
  };

  const onSubmit = (data: ParentFormValues) => {
    const parent = createParentData(data);
    onParentAdded(parent, false); // false = don't continue to details
    resetForm();
  };

  const onSubmitAndContinue = (data: ParentFormValues) => {
    const parent = createParentData(data);
    onParentAdded(parent, true); // true = continue to details
    resetForm();
  };

  const handleCancel = () => {
    resetForm();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add New Parent</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input id="firstName" {...register('firstName')} />
              {errors.firstName && (
                <p className="text-sm text-red-500">{errors.firstName.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input id="lastName" {...register('lastName')} />
              {errors.lastName && (
                <p className="text-sm text-red-500">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" {...register('email')} />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input 
              id="phone" 
              value={formattedPhone}
              onChange={handlePhoneChange}
              placeholder="(555) 123-4567"
              maxLength={14}
            />
            {errors.phone && (
              <p className="text-sm text-red-500">{errors.phone.message}</p>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox 
              id="isPrimary" 
              checked={isPrimary}
              onCheckedChange={(checked) => setValue('isPrimary', checked)}
            />
            <Label htmlFor="isPrimary">Primary Parent</Label>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Adding...' : 'Add Parent'}
            </Button>
            <Button 
              type="button" 
              onClick={handleSubmit(onSubmitAndContinue)}
              disabled={isLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isLoading ? 'Adding...' : 'Add & Edit Full Details'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddParentDialog;
